import { Component, OnInit, Pipe, PipeTransform, AfterViewInit } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { SharedService } from 'src/app/shared.service';
import { ManagementServiceService } from 'src/app/management-service.service';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Subject } from 'rxjs';
import { debounceTime, finalize, distinctUntilChanged  } from 'rxjs/operators';
import { B2BService } from 'src/app/b2-b.service';
import { HostListener } from '@angular/core';
import {
  ColDef,
  GridApi,
  ColumnApi,
  PasteStartEvent,
  PasteEndEvent
} from 'ag-grid-community';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ManagementComponent } from '../management.component';
import { FormControl } from '@angular/forms';
import * as moment from 'moment';
import * as L from 'leaflet';
import { ChartDataSets, ChartOptions, ChartType } from 'chart.js';
import { Color, Label } from 'ng2-charts';

// Pipe to transform event lines to AG Grid row format
@Pipe({
  name: 'eventLinesToGridRows'
})
export class EventLinesToGridRowsPipe implements PipeTransform {
  transform(lines: any[]): any[] {
    if (!lines || !Array.isArray(lines)) {
      return [];
    }

    return lines.map((line: any) => ({
      index: line.GridIndex,
      brand: line.Brand,
      partNumber: line.PartNumber,
      name: line.Name,
      quantity: line.Qty,
      officialListPrice: line.ListPrice,
      officialDiscount: line.Discount,
      price: line.FinalPrice,
      total: line.Total,
      eta: line.StandardDelivery,
      hscode: line.HSCode,
      coo: line.COO,
      weight: line.Weight,
      datasheet: line.DatasheetLink
    }));
  }
}

@Component({
  selector: 'app-b2btracking',
  templateUrl: './b2btracking.component.html',
  styleUrls: ['./b2btracking.component.css']
})
export class B2btrackingComponent implements OnInit, AfterViewInit {

  constructor(
    public parent: ManagementComponent,
    private managementService: ManagementServiceService,
    private B2BService: B2BService,
    private service: SharedService,
    private cookieService: CookieService,
    public translate: TranslateService,
    private router: Router,
    private _bottomSheet: MatBottomSheet,
    private route: ActivatedRoute
  ) { }

  // Main tab management (Searches / Statistics / Screenshots)
  mainTab: 'searches' | 'statistics' | 'screenshots' = 'searches';

  // Sub-tab management (List / Map)
  activeTab: 'list' | 'map' = 'list';

  // Filter panel
  showFilterPanel: boolean = false;
  Filter: any = {};
  FromDate = new FormControl(moment());
  ToDate = new FormControl(moment());

  // Custom dropdown states
  showEventTypeDropdown: boolean = false;

  // Data
  TrackingGrids: any[] = [];
  SelectedGrid: any = null;
  loading: boolean = false;
  loadingGridLines: boolean = false;

  // Grouped events by GridID
  GroupedEvents: any[] = [];

  // Debounce timer for search fields
  searchDebounceTimer: any = null;

  // AG Grid for selected event
  gridApi: GridApi;
  columnApi: ColumnApi;
  columnDefs: ColDef[] = [];
  rowData: any[] = [];

  gridOptions = {
    rowHeight: 30,
    headerHeight: 30,
    domLayout: 'autoHeight',
    suppressCellFocus: true,
    animateRows: true
  };

  // Map properties
  map: L.Map;
  markers: L.CircleMarker[] = [];
  selectedMapEvent: any = null;
  partnerColorMap: Map<number, string> = new Map(); // Cache colors for each PartnerID

  // Color pool for partners (10 colors that repeat)
  colorPool: string[] = [
    '#FF6B6B', // Red
    '#4ECDC4', // Teal
    '#45B7D1', // Blue
    '#96CEB4', // Green
    '#FFEAA7', // Yellow
    '#DDA0DD', // Plum
    '#98D8C8', // Mint
    '#F7DC6F', // Light Yellow
    '#BB8FCE', // Light Purple
    '#85C1E9'  // Light Blue
  ];

  // Statistics Tab Properties
  loadingPartnerStats: boolean = false;
  loadingBrandStats: boolean = false;
  loadingTimeSeriesStats: boolean = false;

  // Statistics filter
  StatsFilter: any = {};
  StatsFromDate = new FormControl(moment().subtract(30, 'days'));
  StatsToDate = new FormControl(moment());
  statsTop: number = 10;
  statsGranularity: number = 0; // 0=Day, 1=Week, 2=Month

  // Statistics dropdown toggles
  showTopNDropdown: boolean = false;
  showGranularityDropdown: boolean = false;

  // Partner Chart
  partnerChartLabels: Label[] = [];
  partnerChartData: ChartDataSets[] = [];
  partnerChartType: ChartType = 'bar';
  partnerChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      xAxes: [{ ticks: { beginAtZero: true } }],
      yAxes: [{}]
    },
    plugins: { legend: { display: false } }
  };
  partnerChartColors: Color[] = [{ backgroundColor: '#4ECDC4' }];

  // Brand Chart
  brandChartLabels: Label[] = [];
  brandChartData: ChartDataSets[] = [];
  brandChartType: ChartType = 'bar';
  brandChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      xAxes: [{ ticks: { beginAtZero: true } }],
      yAxes: [{}]
    },
    plugins: { legend: { display: false } }
  };
  brandChartColors: Color[] = [{ backgroundColor: '#FF6B6B' }];

  // Time Series Chart
  timeSeriesChartLabels: Label[] = [];
  timeSeriesChartData: ChartDataSets[] = [];
  timeSeriesChartType: ChartType = 'line';
  timeSeriesChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      xAxes: [{}],
      yAxes: [{ ticks: { beginAtZero: true } }]
    }
  };
  timeSeriesChartColors: Color[] = [{
    borderColor: '#45B7D1',
    backgroundColor: 'rgba(69, 183, 209, 0.2)',
    pointBackgroundColor: '#45B7D1',
    pointBorderColor: '#fff'
  }];

  // ========== VALUES SECTION ==========
  // Values filter with Period vs FromDate/ToDate mutual exclusivity
  ValuesFromDate = new FormControl(moment().subtract(30, 'days'));
  ValuesToDate = new FormControl(moment());
  valuesTop: number = 10;
  valuesGranularity: number = 0; // 0=Day, 1=Week, 2=Month
  valuesPeriod: any = null; // Selected period object (null if using FromDate/ToDate)

  // Period dropdown options
  periodOptions: any[] = [
    { ID: 1, Name: 'Today' },
    { ID: 2, Name: 'Current Week' },
    { ID: 3, Name: 'Current Month' },
    { ID: 4, Name: 'Current Year' },
    { ID: 5, Name: 'Yesterday' },
    { ID: 6, Name: 'Last Week' },
    { ID: 7, Name: 'Last Month' },
    { ID: 8, Name: 'Last Year' }
  ];

  // Values dropdown toggles
  showValuesTopNDropdown: boolean = false;
  showValuesGranularityDropdown: boolean = false;
  showValuesPeriodDropdown: boolean = false;

  // Values loading states
  loadingValuesPartner: boolean = false;
  loadingValuesBrand: boolean = false;
  loadingValuesTimeSeries: boolean = false;

  // Values Partner Chart
  valuesPartnerChartLabels: Label[] = [];
  valuesPartnerChartData: ChartDataSets[] = [];
  valuesPartnerChartType: ChartType = 'bar';
  valuesPartnerChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      xAxes: [{ ticks: { beginAtZero: true } }],
      yAxes: [{}]
    },
    plugins: { legend: { display: false } }
  };
  valuesPartnerChartColors: Color[] = [{ backgroundColor: '#9B59B6' }];

  // Values Brand Chart
  valuesBrandChartLabels: Label[] = [];
  valuesBrandChartData: ChartDataSets[] = [];
  valuesBrandChartType: ChartType = 'bar';
  valuesBrandChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      xAxes: [{ ticks: { beginAtZero: true } }],
      yAxes: [{}]
    },
    plugins: { legend: { display: false } }
  };
  valuesBrandChartColors: Color[] = [{ backgroundColor: '#E74C3C' }];

  // Values Time Series Chart
  valuesTimeSeriesChartLabels: Label[] = [];
  valuesTimeSeriesChartData: ChartDataSets[] = [];
  valuesTimeSeriesChartType: ChartType = 'line';
  valuesTimeSeriesChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      xAxes: [{}],
      yAxes: [{ ticks: { beginAtZero: true } }]
    }
  };
  valuesTimeSeriesChartColors: Color[] = [{
    borderColor: '#27AE60',
    backgroundColor: 'rgba(39, 174, 96, 0.2)',
    pointBackgroundColor: '#27AE60',
    pointBorderColor: '#fff'
  }];

  // Statistics Partner Filter
  StatsPartnerSearchTerm: string = '';
  StatsPartners: any[] = [];
  StatsSelectedPartner: any = null;

  ngOnInit(): void {
    this.InitializeFilter();
    this.InitializeColumnDefs();
    this.GetB2BUserTrackingGridsFiltered();
    this.FixLeafletIconPaths();
  }

  ngAfterViewInit(): void {
    // Map will be initialized when user switches to map tab
  }

  // Fix Leaflet default icon paths for Angular
  FixLeafletIconPaths() {
    const iconRetinaUrl = 'assets/marker-icon-2x.png';
    const iconUrl = 'assets/marker-icon.png';
    const shadowUrl = 'assets/marker-shadow.png';
    const iconDefault = L.icon({
      iconRetinaUrl,
      iconUrl,
      shadowUrl,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28],
      shadowSize: [41, 41]
    });
    L.Marker.prototype.options.icon = iconDefault;
  }

  // Tab switching
  SwitchTab(tab: 'list' | 'map') {
    this.activeTab = tab;

    if (tab === 'map') {
      // Initialize map after a short delay to ensure DOM is ready
      setTimeout(() => {
        const mapElement = document.getElementById('tracking-map');
        console.log('Map element found:', mapElement);
        if (mapElement) {
          this.InitializeMap();
        } else {
          console.error('Map container element not found!');
        }
      }, 250);
    }
  }

  // Get events with coordinates for map
  GetEventsWithCoordinates(): any[] {
    const filtered = this.TrackingGrids.filter(event => {
      const hasLat = event.Latitude != null && event.Latitude !== undefined;
      const hasLng = event.Longitude != null && event.Longitude !== undefined;

      if (hasLat && hasLng) {
        console.log(`Event ${event.EventID} has coords: ${event.Latitude}, ${event.Longitude}`);
      }

      return hasLat && hasLng;
    });

    console.log(`Total events: ${this.TrackingGrids.length}, With coordinates: ${filtered.length}`);
    return filtered;
  }

  // Initialize Leaflet map
  InitializeMap() {
    try {
      console.log('InitializeMap called');

      if (this.map) {
        console.log('Removing existing map');
        this.map.remove();
        this.map = null;
      }

      const eventsWithCoords = this.GetEventsWithCoordinates();
      console.log('Events with coordinates:', eventsWithCoords);

      // Create map centered on Europe
      console.log('Creating Leaflet map...');
      this.map = L.map('tracking-map', {
        center: [48.8566, 2.3522],
        zoom: 4,
        scrollWheelZoom: true,
        preferCanvas: false
      });

      console.log('Map created:', this.map);

      // Use CartoDB tiles - more reliable than OSM
      console.log('Adding tile layer...');
      const tileLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 20,
        minZoom: 2
      });

      tileLayer.on('tileerror', (error) => {
        console.error('Tile loading error:', error);
      });

      tileLayer.on('tileload', () => {
        console.log('Tile loaded successfully');
      });

      tileLayer.addTo(this.map);
      console.log('Tile layer added');

      // Force map to invalidate size after a short delay
      setTimeout(() => {
        if (this.map) {
          console.log('Invalidating map size...');
          this.map.invalidateSize();
          this.AddMarkersToMap();
        }
      }, 300);
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  }

  // Add markers to map
  AddMarkersToMap() {
    if (!this.map) {
      console.warn('Map not initialized yet');
      return;
    }

    // Clear existing markers
    this.markers.forEach(marker => marker.remove());
    this.markers = [];

    const eventsWithCoords = this.GetEventsWithCoordinates();
    console.log('Adding markers for events:', eventsWithCoords);

    if (eventsWithCoords.length === 0) {
      console.warn('No events with coordinates to display');
      return;
    }

    eventsWithCoords.forEach(event => {
      console.log(`Adding marker at: ${event.Latitude}, ${event.Longitude}`);

      // Get unique color for this partner
      const partnerColor = this.GetColorForPartner(event.PartnerID);
      const customerName = this.TruncateCustomerName(event.PartnerName);

      console.log(`Partner ${event.PartnerName} (ID: ${event.PartnerID}) -> Color: ${partnerColor}`);

      // Create a custom marker with both circle and label combined
      const customMarker = L.marker([event.Latitude, event.Longitude], {
        icon: L.divIcon({
          className: 'custom-marker',
          html: `
            <div style="position: relative; text-align: center;">
              <!-- Customer name label -->
              <div style="
                background: rgba(255, 255, 255, 0.95);
                border: 1px solid ${partnerColor};
                border-radius: 3px;
                padding: 1px 4px;
                font-size: 9px;
                font-weight: 600;
                color: #333;
                white-space: nowrap;
                box-shadow: 0 1px 2px rgba(0,0,0,0.2);
                margin-bottom: 2px;
                display: inline-block;
              ">${customerName}</div>
              <!-- Colored circle marker -->
              <div style="
                width: 20px !important;
                height: 20px !important;
                background: ${partnerColor} !important;
                background-color: ${partnerColor} !important;
                border: 2px solid #ffffff !important;
                border-radius: 50% !important;
                box-shadow: 0 2px 6px rgba(0,0,0,0.4) !important;
                margin: 0 auto !important;
                position: relative !important;
                display: block !important;
                opacity: 1 !important;
              "></div>
            </div>
          `,
          iconSize: [40, 40],
          iconAnchor: [20, 35] // Center horizontally, bottom of circle vertically
        })
      })
        .addTo(this.map)
        .bindPopup(this.CreateMarkerPopup(event))
        .on('click', () => {
          this.OnMarkerClick(event);
        });

      this.markers.push(customMarker as any);
    });

    console.log(`Added ${this.markers.length} markers to map`);

    // Fit map to show all markers
    if (this.markers.length > 0) {
      const group = L.featureGroup(this.markers);
      this.map.fitBounds(group.getBounds().pad(0.1));
    }
  }

  // Create popup content for marker
  CreateMarkerPopup(event: any): string {
    return `
      <div style="min-width: 200px;">
        <strong>${event.EventType?.Name || 'Event'}</strong><br/>
        <small>${event.EventRegisterUI || ''}</small><br/>
        <strong>Partner:</strong> ${event.PartnerName || 'N/A'}<br/>
        <strong>User:</strong> ${event.IncomingEmailAddress || 'N/A'}<br/>
        <strong>GridID:</strong> ${event.GridID || 'N/A'}<br/>
        <em>Click to view details</em>
      </div>
    `;
  }

  // Handle marker click
  OnMarkerClick(event: any) {
    this.selectedMapEvent = event;
    this.GetB2BUserTrackingGridLines(event.GridID);

    // Scroll to show the event details below the map
    setTimeout(() => {
      const eventDetails = document.querySelector('.map-event-details') as HTMLElement;
      if (eventDetails) {
        eventDetails.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150); // Small delay to ensure the details panel is rendered
  }

  // Close map event details
  CloseMapEventDetails() {
    this.selectedMapEvent = null;
    this.GroupedEvents = [];
  }

  // Generate a unique color for each PartnerID using color pool
  GetColorForPartner(partnerID: number): string {
    // Check if we already have a color for this partner
    if (this.partnerColorMap.has(partnerID)) {
      return this.partnerColorMap.get(partnerID);
    }

    // Use color pool - cycle through the 10 colors
    const colorIndex = partnerID % this.colorPool.length;
    const color = this.colorPool[colorIndex];

    // Cache the color
    this.partnerColorMap.set(partnerID, color);

    return color;
  }

  // Truncate customer name to 8 characters + "..."
  TruncateCustomerName(name: string): string {
    if (!name) return 'N/A';
    if (name.length <= 8) return name;
    return name.substring(0, 8) + '...';
  }

  InitializeFilter() {
    this.Filter = {};
    this.Filter.EventType = null;
    this.Filter.FromDate = null;
    this.Filter.ToDate = null;
    this.Filter.Partner = null;
    this.Filter.Country = null;
    this.Filter.User = null;
    this.Filter.Status = null;
    this.Filter.GridID = '';
    this.Filter.Brand = '';
    this.Filter.PartNumber = '';
    this.Filter.ProductName = '';

    this.Filter.Pager = {};
    this.Filter.Pager.PageIndex = 1;
    this.Filter.Pager.PageSize = 50;
    this.Filter.Pager.TotalCount = 0;
    this.Filter.Pager.Pages = [];
  }

  ToggleFilterPanel() {
    this.showFilterPanel = !this.showFilterPanel;
  }

  ApplyFilter() {
    this.Filter.Pager.PageIndex = 1;
    this.GetB2BUserTrackingGridsFiltered();
    this.showFilterPanel = false;
  }

  ClearFilter() {
    this.InitializeFilter();
    this.FromDate = new FormControl(moment());
    this.ToDate = new FormControl(moment());
    this.GetB2BUserTrackingGridsFiltered();
  }

  OnSearchFieldChange() {
    // Clear existing timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    // Set new timer for 250ms
    this.searchDebounceTimer = setTimeout(() => {
      console.log('Search field changed, triggering filter...');
      this.GetB2BUserTrackingGridsFiltered(1);
    }, 250);
  }

  ClearSearchField(fieldName: string) {
    // Clear the specific field
    if (fieldName === 'Brand') {
      this.Filter.Brand = null;
    } else if (fieldName === 'PartNumber') {
      this.Filter.PartNumber = null;
    } else if (fieldName === 'ProductName') {
      this.Filter.ProductName = null;
    }

    // Immediately trigger search
    this.GetB2BUserTrackingGridsFiltered(1);
  }

  GetB2BUserTrackingGridsFiltered(page = 1) {
    this.Filter.Pager.PageIndex = page;

    // Create a clean copy of the filter for the request (without the Pages array)
    const requestFilter = {
      ...this.Filter,
      Pager: {
        PageIndex: this.Filter.Pager.PageIndex,
        PageSize: this.Filter.Pager.PageSize,
        TotalCount: this.Filter.Pager.TotalCount
        // Don't send Pages array to backend
      }
    };

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Filter: requestFilter,
      MasterData: this.parent.masterData
    };

    if (this.Filter.FromDate != null) {
      request.Filter.FromDate = moment(this.FromDate.value._d).format('YYYY-MM-DD');
    }

    if (this.Filter.ToDate != null) {
      request.Filter.ToDate = moment(this.ToDate.value._d).format('YYYY-MM-DD');
    }

    this.loading = true;

    // Close any open map event details when filtering
    this.selectedMapEvent = null;
    this.GroupedEvents = [];

    this.B2BService.GetB2BUserTrackingGridsFiltered(request).subscribe(response => {
      console.log('B2B Tracking Response:', response);

      if (response["Status"] != 1) {
        this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      } else if (response["Status"] == 1) {
        this.TrackingGrids = response["Grids"];
        console.log('Tracking Grids:', this.TrackingGrids);
        this.Filter.Pager.TotalCount = response["Filter"]?.Pager?.TotalCount || 0;

        // Convert Pages from number to array [1, 2, 3, ...]
        const totalPages = response["Filter"]?.Pager?.Pages || 0;
        this.Filter.Pager.Pages = totalPages > 0 ? Array.from({ length: totalPages }, (_, i) => i + 1) : [];

        // Update map markers if on map tab
        if (this.activeTab === 'map' && this.map) {
          console.log('Refreshing map markers after filter...');
          this.AddMarkersToMap();
        }
      }

      this.loading = false;
    });
  }

  SelectGrid(grid: any) {
    console.log('SelectGrid called with:', grid);

    // Toggle: if clicking the same grid, collapse it
    if (this.SelectedGrid?.EventID === grid.EventID) {
      this.SelectedGrid = null;
      this.GroupedEvents = [];
      console.log('Grid collapsed');
      return;
    }

    // Otherwise, expand the new grid
    this.SelectedGrid = grid;
    this.GetB2BUserTrackingGridLines(grid.GridID);
  }

  GetB2BUserTrackingGridLines(gridID: string) {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      GridID: gridID,
      Brand: this.Filter.Brand || null,
      PartNumber: this.Filter.PartNumber || null,
      ProductName: this.Filter.ProductName || null
    };

    this.loadingGridLines = true;
    this.GroupedEvents = [];

    this.B2BService.GetB2BUserTrackingGridLines(request).subscribe(response => {
      console.log('GetB2BUserTrackingGridLines Response:', response);

      if (response["Status"] != 1) {
        this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      } else if (response["Status"] == 1) {
        // Response contains array of tracking events grouped by EventID
        this.GroupedEvents = response["Trackings"] || [];
        console.log('Grouped Events:', this.GroupedEvents);
      }

      this.loadingGridLines = false;
    });
  }

  InitializeColumnDefs() {
    this.columnDefs = [
      {
        headerName: '#',
        field: 'index',
        editable: false,
        minWidth: 50,
        flex: 0.5,
        suppressMenu: true
      },
      {
        headerName: 'Brand',
        field: 'brand',
        editable: false,
        minWidth: 125,
        flex: 0.75,
        suppressMenu: true
      },
      {
        headerName: 'PartNumber',
        field: 'partNumber',
        editable: false,
        minWidth: 100,
        flex: 1,
        suppressMenu: true
      },
      {
        headerName: 'Name',
        field: 'name',
        editable: false,
        minWidth: 250,
        flex: 14,
        suppressMenu: true
      },
      {
        headerName: 'Qty',
        field: 'quantity',
        editable: false,
        type: 'numericColumn',
        minWidth: 60,
        flex: 0.75,
        suppressMenu: true
      },
      {
        headerName: 'List-Price',
        field: 'officialListPrice',
        editable: false,
        minWidth: 90,
        flex: 2,
        suppressMenu: true,
        valueFormatter: (params: any) => {
          if (params.value != null) {
            return params.value.toFixed(2) + ' €';
          }
          return '';
        }
      },
      {
        headerName: 'Discount',
        field: 'officialDiscount',
        editable: false,
        minWidth: 75,
        flex: 0.75,
        suppressMenu: true,
        type: 'rightAligned',
        valueFormatter: (params: any) => {
          if (params.value != null) {
            return params.value + '%';
          }
          return '';
        }
      },
      {
        headerName: 'Final-Price',
        field: 'price',
        editable: false,
        minWidth: 100,
        flex: 2,
        suppressMenu: true,
        valueFormatter: (params: any) => {
          if (params.value != null) {
            return params.value.toFixed(2) + ' €';
          }
          return '';
        }
      },
      {
        headerName: 'Total',
        field: 'total',
        editable: false,
        minWidth: 90,
        flex: 2,
        suppressMenu: true,
        valueFormatter: (params: any) => {
          if (params.value != null) {
            return params.value.toFixed(2) + ' €';
          }
          return '';
        }
      },
      {
        headerName: 'Standard-Delivery',
        field: 'eta',
        editable: false,
        minWidth: 140,
        flex: 2,
        suppressMenu: true
      },
      {
        headerName: 'HSCode',
        field: 'hscode',
        editable: false,
        minWidth: 100,
        flex: 0.75,
        suppressMenu: true
      },
      {
        headerName: 'COO',
        field: 'coo',
        editable: false,
        minWidth: 75,
        flex: 0.75,
        suppressMenu: true
      },
      {
        headerName: 'Weight',
        field: 'weight',
        editable: false,
        minWidth: 75,
        flex: 0.75,
        suppressMenu: true
      }
    ];
  }

  onGridReady(params: any) {
    console.log('AG Grid Ready event fired');
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
    console.log('Grid API:', this.gridApi);
    console.log('Current rowData:', this.rowData);
    this.gridApi.sizeColumnsToFit();
  }

  GetEventTypeName(eventType: any): string {
    if (!eventType) return 'Unknown';
    switch (eventType.ID) {
      case 1: return 'Keyboard';
      case 2: return 'Copy/Paste (Keyboard)';
      case 3: return 'Copy/Paste (Mouse)';
      case 4: return 'Export PDF';
      case 5: return 'Export XLSX';
      default: return eventType.Name || 'Unknown';
    }
  }

  FormatDate(date: any): string {
    if (!date) return '';
    return moment(date).format('YYYY-MM-DD HH:mm:ss');
  }

  // Format number with space separators (e.g., 177749 -> "177 749")
  FormatNumber(num: number): string {
    if (num === null || num === undefined) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  // Pagination
  ChangePage(page: number) {
    this.GetB2BUserTrackingGridsFiltered(page);
  }

  // Partner search
  Partners: any[] = [];
  PartnerSearchTerm: string = '';

  Countries:any[] = [];
  CountrySearchTerm: string = '';

  GetCountriesFiltered(searchTerm: string) {
    //filter from masterdata
    this.Countries = this.parent.masterData.Countries.filter(x => x.Name.toLowerCase().startsWith(searchTerm.toLowerCase()));
  }

  SelectCountry(country: any) {
    this.Filter.Country = country;
    this.CountrySearchTerm = country.Name;
    this.Countries = [];
  }

  ClearCountry(){
    this.Filter.Country = null;
    this.CountrySearchTerm = '';
    this.Countries = [];
  }

  GetPartnersFiltered(searchTerm: string) {
    if (!searchTerm || searchTerm.length < 2) {
      this.Partners = [];
      return;
    }

    const request = {
      SearchTerm: searchTerm,
      CountryID: null,
      PageIndex: 1,
      PageSize: 50,
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      CompanyID: null
    };

    this.service.GetPartnersFiltered(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.Partners = response["Partners"];
      }
    });
  }

  SelectPartner(partner: any) {
    this.Filter.Partner = partner;
    this.PartnerSearchTerm = partner.Name;
    this.Partners = [];
  }

  ClearPartner() {
    this.Filter.Partner = null;
    this.PartnerSearchTerm = '';
    this.Partners = [];
  }

  // Statistics Partner search
  GetStatsPartnersFiltered(searchTerm: string) {
    if (!searchTerm || searchTerm.length < 2) {
      this.StatsPartners = [];
      return;
    }

    const request = {
      SearchTerm: searchTerm,
      CountryID: null,
      PageIndex: 1,
      PageSize: 50,
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      CompanyID: null
    };

    this.service.GetPartnersFiltered(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.StatsPartners = response["Partners"];
      }
    });
  }

  SelectStatsPartner(partner: any) {
    this.StatsSelectedPartner = partner;
    this.StatsPartnerSearchTerm = partner.Name;
    this.StatsPartners = [];
    // Reload all statistics with the selected partner
    this.LoadAllStatistics();
    this.LoadAllValues();
  }

  ClearStatsPartner() {
    this.StatsSelectedPartner = null;
    this.StatsPartnerSearchTerm = '';
    this.StatsPartners = [];
    // Reload all statistics without partner filter
    this.LoadAllStatistics();
    this.LoadAllValues();
  }

  // User search
  Users: any[] = [];
  UserSearchTerm: string = '';

  GetUsersFiltered(searchTerm: string) {
    if (!searchTerm || searchTerm.length < 2) {
      this.Users = [];
      return;
    }

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      SearchTerm: searchTerm
    };

    this.service.GetUsersFiltered(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.Users = response["Users"];
      }
    });
  }

  SelectUser(user: any) {
    this.Filter.User = user;
    this.UserSearchTerm = user.Email;
    this.Users = [];
  }

  ClearUser() {
    this.Filter.User = null;
    this.UserSearchTerm = '';
    this.Users = [];
  }

  // Event Type dropdown
  SelectEventType(eventType: any) {
    this.Filter.EventType = eventType;
    this.showEventTypeDropdown = false;
  }

  // Statistics Tab Methods
  OnStatisticsTabClick() {
    // Load statistics when switching to Statistics tab
    this.LoadAllStatistics();
    this.LoadAllValues();
  }

  LoadAllStatistics() {
    this.GetPartnerStatistics();
    this.GetBrandStatistics();
    this.GetTimeSeriesStatistics();
  }

  GetPartnerStatistics() {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Filter: {
        FromDate: moment(this.StatsFromDate.value).format('YYYY-MM-DD'),
        ToDate: moment(this.StatsToDate.value).format('YYYY-MM-DD'),
        Partner: this.StatsSelectedPartner,
        Country: null
      },
      Top: this.statsTop,
      IncludeUnknownPartner: false
    };

    this.loadingPartnerStats = true;

    this.B2BService.GetB2BTrackingSearchCountsByPartner(request).subscribe(response => {
      if (response["Status"] == 1) {
        const rows = response["Rows"] || [];
        this.partnerChartLabels = rows.map((r: any) => r.PartnerName || r.PartnerID || 'Unknown');
        this.partnerChartData = [{
          data: rows.map((r: any) => r.SearchesCount),
          label: 'Searches'
        }];
      } else {
        this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      }
      this.loadingPartnerStats = false;
    });
  }

  GetBrandStatistics() {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Filter: {
        FromDate: moment(this.StatsFromDate.value).format('YYYY-MM-DD'),
        ToDate: moment(this.StatsToDate.value).format('YYYY-MM-DD'),
        Partner: this.StatsSelectedPartner,
        Country: null
      },
      Top: this.statsTop,
      IncludeUnknownBrand: false
    };

    this.loadingBrandStats = true;

    this.B2BService.GetB2BTrackingSearchCountsByBrand(request).subscribe(response => {
      if (response["Status"] == 1) {
        const rows = response["Rows"] || [];
        this.brandChartLabels = rows.map((r: any) => r.BrandName || r.BrandID || 'Unknown');
        this.brandChartData = [{
          data: rows.map((r: any) => r.SearchesCount),
          label: 'Searches'
        }];
      } else {
        this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      }
      this.loadingBrandStats = false;
    });
  }

  GetTimeSeriesStatistics() {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Filter: {
        FromDate: moment(this.StatsFromDate.value).format('YYYY-MM-DD'),
        ToDate: moment(this.StatsToDate.value).format('YYYY-MM-DD'),
        Partner: this.StatsSelectedPartner,
        Country: null
      },
      Granularity: this.statsGranularity
    };

    this.loadingTimeSeriesStats = true;

    this.B2BService.GetB2BTrackingSearchCountsTimeSeries(request).subscribe(response => {
      if (response["Status"] == 1) {
        const rows = response["Rows"] || [];
        this.timeSeriesChartLabels = rows.map((r: any) => moment(r.PeriodStart).format('YYYY-MM-DD'));
        this.timeSeriesChartData = [{
          data: rows.map((r: any) => r.SearchesCount),
          label: 'Searches',
          fill: true
        }];
      } else {
        this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      }
      this.loadingTimeSeriesStats = false;
    });
  }

  OnStatsFilterChange() {
    this.LoadAllStatistics();
  }

  SelectStatsTop(value: number) {
    this.statsTop = value;
    this.showTopNDropdown = false;
    this.LoadAllStatistics();
  }

  SelectStatsGranularity(value: number) {
    this.statsGranularity = value;
    this.showGranularityDropdown = false;
    this.LoadAllStatistics();
  }

  GetGranularityLabel(): string {
    switch (this.statsGranularity) {
      case 0: return this.translate.instant('B2BTracking.Daily');
      case 1: return this.translate.instant('B2BTracking.Weekly');
      case 2: return this.translate.instant('B2BTracking.Monthly');
      default: return this.translate.instant('B2BTracking.Daily');
    }
  }

  // ========== VALUES METHODS ==========

  // Mutual exclusivity: When Period is selected, clear FromDate/ToDate
  OnValuesPeriodChange(period: any) {
    this.valuesPeriod = period;
    this.ValuesFromDate.setValue(null);
    this.ValuesToDate.setValue(null);
    this.showValuesPeriodDropdown = false;
    this.LoadAllValues();
  }

  // Mutual exclusivity: When FromDate/ToDate is changed, clear Period
  OnValuesDateChange() {
    this.valuesPeriod = null;
    this.LoadAllValues();
  }

  ClearValuesPeriod() {
    this.valuesPeriod = null;
    this.showValuesPeriodDropdown = false;
  }

  SelectValuesTop(value: number) {
    this.valuesTop = value;
    this.showValuesTopNDropdown = false;
    this.LoadAllValues();
  }

  SelectValuesGranularity(value: number) {
    this.valuesGranularity = value;
    this.showValuesGranularityDropdown = false;
    this.LoadAllValues();
  }

  GetValuesGranularityLabel(): string {
    switch (this.valuesGranularity) {
      case 0: return this.translate.instant('B2BTracking.Daily');
      case 1: return this.translate.instant('B2BTracking.Weekly');
      case 2: return this.translate.instant('B2BTracking.Monthly');
      default: return this.translate.instant('B2BTracking.Daily');
    }
  }

  LoadAllValues() {
    this.GetValuesByPartner();
    this.GetValuesByBrand();
    this.GetValuesTimeSeries();
  }

  GetValuesByPartner() {
    const request: any = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Filter: {
        FromDate: this.ValuesFromDate.value ? moment(this.ValuesFromDate.value).format('YYYY-MM-DD') : null,
        ToDate: this.ValuesToDate.value ? moment(this.ValuesToDate.value).format('YYYY-MM-DD') : null,
        Period: this.valuesPeriod,
        Partner: this.StatsSelectedPartner,
        Country: null
      },
      Top: this.valuesTop,
      IncludeUnknownPartner: false
    };

    this.loadingValuesPartner = true;

    this.B2BService.GetB2BTrackingValuesByPartner(request).subscribe(response => {
      if (response["Status"] == 1) {
        const rows = response["Rows"] || [];
        this.valuesPartnerChartLabels = rows.map((r: any) => r.PartnerName || r.PartnerID || 'Unknown');
        this.valuesPartnerChartData = [{
          data: rows.map((r: any) => r.TotalValue),
          label: 'Values'
        }];
      } else {
        this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      }
      this.loadingValuesPartner = false;
    });
  }

  GetValuesByBrand() {
    const request: any = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Filter: {
        FromDate: this.ValuesFromDate.value ? moment(this.ValuesFromDate.value).format('YYYY-MM-DD') : null,
        ToDate: this.ValuesToDate.value ? moment(this.ValuesToDate.value).format('YYYY-MM-DD') : null,
        Period: this.valuesPeriod,
        Partner: this.StatsSelectedPartner,
        Country: null
      },
      Top: this.valuesTop,
      IncludeUnknownBrand: false
    };

    this.loadingValuesBrand = true;

    this.B2BService.GetB2BTrackingValuesByBrand(request).subscribe(response => {
      if (response["Status"] == 1) {
        const rows = response["Rows"] || [];
        this.valuesBrandChartLabels = rows.map((r: any) => r.BrandName || 'Unknown');
        this.valuesBrandChartData = [{
          data: rows.map((r: any) => r.TotalValue),
          label: 'Values'
        }];
      } else {
        this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      }
      this.loadingValuesBrand = false;
    });
  }

  GetValuesTimeSeries() {
    const request: any = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Filter: {
        FromDate: this.ValuesFromDate.value ? moment(this.ValuesFromDate.value).format('YYYY-MM-DD') : null,
        ToDate: this.ValuesToDate.value ? moment(this.ValuesToDate.value).format('YYYY-MM-DD') : null,
        Period: this.valuesPeriod,
        Partner: this.StatsSelectedPartner,
        Country: null
      },
      Granularity: this.valuesGranularity
    };

    this.loadingValuesTimeSeries = true;

    this.B2BService.GetB2BTrackingValuesTimeSeries(request).subscribe(response => {
      if (response["Status"] == 1) {
        const rows = response["Rows"] || [];
        this.valuesTimeSeriesChartLabels = rows.map((r: any) => moment(r.PeriodStart).format('YYYY-MM-DD'));
        this.valuesTimeSeriesChartData = [{
          data: rows.map((r: any) => r.TotalValue),
          label: 'Values',
          fill: true
        }];
      } else {
        this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      }
      this.loadingValuesTimeSeries = false;
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // Screenshots Tab
  // ═══════════════════════════════════════════════════════════════

  // Screenshot data
  Screenshots: any[] = [];
  loadingScreenshots: boolean = false;
  ScreenshotPager: any = { PageIndex: 1, PageSize: 50, Pages: 1, TotalCount: 0 };

  // Screenshot filters
  ScreenshotFromDate = new FormControl(moment().subtract(30, 'days'));
  ScreenshotToDate = new FormControl(moment());
  ScreenshotPartnerSearchTerm: string = '';
  ScreenshotSelectedPartner: any = null;
  ScreenshotPartners: any[] = [];
  ScreenshotTypeFilter: any = null;
  showScreenshotTypeDropdown: boolean = false;

  OnScreenshotsTabClick() {
    if (this.Screenshots.length === 0) {
      this.GetScreenshotsFiltered(1);
    }
  }

  GetScreenshotsFiltered(pageIndex: number = 1) {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerID: this.ScreenshotSelectedPartner?.ID || null,
      UserID: null,
      FromDate: this.ScreenshotFromDate.value ? this.ScreenshotFromDate.value.toISOString() : null,
      ToDate: this.ScreenshotToDate.value ? this.ScreenshotToDate.value.toISOString() : null,
      ScreenshotType: this.ScreenshotTypeFilter?.ID || null,
      Pager: {
        PageIndex: pageIndex,
        PageSize: 50
      }
    };

    this.loadingScreenshots = true;

    this.B2BService.GetB2BTrackingScreenshotsFiltered(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.Screenshots = response["Screenshots"] || [];
        this.ScreenshotPager = response["Pager"] || { PageIndex: 1, PageSize: 50, Pages: 1, TotalCount: 0 };
      } else {
        this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      }
      this.loadingScreenshots = false;
    });
  }

  GetScreenshotPartnersFiltered(searchTerm: string) {
    if (!searchTerm || searchTerm.length < 2) {
      this.ScreenshotPartners = [];
      return;
    }

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      SearchTerm: searchTerm,
      PageIndex: 1,
      PageSize: 5
    };

    this.service.SearchPartnersSimplifiedFiltered(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.ScreenshotPartners = response["Partners"] || [];
      }
    });
  }

  SelectScreenshotPartner(partner: any) {
    this.ScreenshotSelectedPartner = partner;
    this.ScreenshotPartnerSearchTerm = partner.Name;
    this.ScreenshotPartners = [];
  }

  ClearScreenshotPartner() {
    this.ScreenshotSelectedPartner = null;
    this.ScreenshotPartnerSearchTerm = '';
    this.ScreenshotPartners = [];
  }

  SelectScreenshotType(type: any) {
    this.ScreenshotTypeFilter = type;
    this.showScreenshotTypeDropdown = false;
  }

  ClearScreenshotFilter() {
    this.ScreenshotFromDate = new FormControl(moment().subtract(30, 'days'));
    this.ScreenshotToDate = new FormControl(moment());
    this.ScreenshotSelectedPartner = null;
    this.ScreenshotPartnerSearchTerm = '';
    this.ScreenshotPartners = [];
    this.ScreenshotTypeFilter = null;
    this.GetScreenshotsFiltered(1);
  }

  getScreenshotTypeIcon(type: number): string {
    switch (type) {
      case 1: return 'screenshot';
      case 2: return 'content_cut';
      case 3: return 'laptop_mac';
      case 4: return 'print';
      case 5: return 'crop';
      default: return 'screenshot_monitor';
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // Screenshot Grid Popup
  // ═══════════════════════════════════════════════════════════════

  SelectedScreenshot: any = null;
  ScreenshotGridEvents: any[] = [];
  loadingScreenshotGrid: boolean = false;

  OpenScreenshotGrid(screenshot: any) {
    if (!screenshot.ParentGridID) {
      this.parent.openSnackBar('No grid session linked to this screenshot', 'OK', 'alert-info-snackbar');
      return;
    }

    this.SelectedScreenshot = screenshot;
    this.ScreenshotGridEvents = [];
    this.loadingScreenshotGrid = true;

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      GridID: screenshot.ParentGridID
    };

    this.B2BService.GetB2BUserTrackingGridLines(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.ScreenshotGridEvents = response["Trackings"] || [];
      } else {
        this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      }
      this.loadingScreenshotGrid = false;
    });
  }

  CloseScreenshotGrid() {
    this.SelectedScreenshot = null;
    this.ScreenshotGridEvents = [];
  }
}
